<template>
<div class="customer-notices">
  <el-popover v-model="previewVisible" placement="bottom-end" width="360" trigger="click" popper-class="customer-message-popover" @show="loadPreview">
    <div v-loading="previewLoading" class="message-preview">
      <div class="message-preview-heading"><strong>{{ $t('消息通知') }}</strong><span v-if="unreadTotal">{{ $t(`${unreadTotal} 条未读`) }}</span></div>
      <div v-if="recentNotices.length" class="message-preview-list">
        <button v-for="item in recentNotices" :key="noticeKey(item)" type="button" class="message-preview-item" @click="openAll(item)">
          <i :class="item.unread ? 'el-icon-message-solid' : 'el-icon-message'" />
          <span><strong data-i18n-ignore>{{ item.title }}</strong><small>{{ messageTypeText(item.notificationType) }} · {{ displayTime(item.createTime) }}</small></span>
          <em v-if="item.unread" :aria-label="$t('未读')" />
        </button>
      </div>
      <el-empty v-else-if="!previewLoading" :description="$t('暂无消息')" :image-size="52" />
      <button type="button" class="message-preview-all" @click="openAll()">{{ $t('查看全部消息') }}<span v-if="previewTotal">（{{ previewTotal }}）</span><i class="el-icon-arrow-right" /></button>
    </div>
    <el-badge slot="reference" :value="unreadTotal" :hidden="!unreadTotal" :max="99" class="notice-badge">
      <el-button type="text" icon="el-icon-bell" :title="$t('消息')" :aria-label="$t('消息')" class="notice-trigger" />
    </el-badge>
  </el-popover>

  <el-dialog :visible.sync="visible" width="760px" append-to-body class="notices-dialog" @closed="loadPreview">
    <span slot="title" class="notices-title">{{ $t('消息中心') }}<small v-if="total">{{ $t(`共 ${total} 条消息`) }}</small></span>
    <div v-loading="loading" class="notices-body">
      <template v-if="notices.length">
        <div class="notice-list" role="list" :aria-label="$t('消息列表')">
          <button v-for="item in notices" :key="noticeKey(item)" type="button" class="notice-item" :class="{ selected: selectedId === noticeKey(item) }" @click="selectNotice(item)">
            <span class="notice-title" data-i18n-ignore>{{ item.title }}</span>
            <span class="notice-meta"><span v-if="item.unread" class="notice-unread">{{ $t('未读') }} · </span>{{ messageTypeText(item.notificationType) }} · {{ displayTime(item.createTime) }}</span>
          </button>
        </div>
        <div class="notice-detail">
          <template v-if="selectedNotice">
            <h3 data-i18n-ignore>{{ selectedNotice.title }}</h3>
            <div class="notice-detail-meta">{{ displayTime(selectedNotice.createTime) }}</div>
            <iframe :key="selectedNotice.id" :srcdoc="noticeDocument" sandbox="" :title="$t('消息内容')" class="notice-content" />
          </template>
        </div>
      </template>
      <el-empty v-else-if="!loading" :description="$t('暂无消息')" />
    </div>
    <div v-if="total" class="notice-pagination">
      <el-pagination background small layout="sizes, prev, pager, next" :current-page="pageNum" :page-size="pageSize" :page-sizes="[20, 30, 50, 100, 500, 1000]" :total="total" @current-change="changePage" @size-change="changeSize" />
    </div>
  </el-dialog>
</div>
</template>

<script>
import { translateUi } from '../../i18n'
import { listCustomerNotifications, markCustomerNotificationRead } from '../../api/system/notice'

export default {
  name: 'CustomerNotices',
  data () {
    return { previewVisible: false, previewLoading: false, recentNotices: [], previewTotal: 0, visible: false, loading: false, notices: [], selectedId: '', total: 0, unreadTotal: 0, pageNum: 1, pageSize: 20, requestId: 0, previewRequestId: 0 }
  },
  computed: {
    selectedNotice () { return this.notices.find(item => this.noticeKey(item) === this.selectedId) },
    noticeDocument () {
      const content = this.selectedNotice ? this.selectedNotice.content || '' : ''
      return `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;color:#303744;font:14px/1.7 Arial,sans-serif;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${content}</body></html>`
    }
  },
  created () { this.loadPreview() },
  methods: {
    openAll (item) {
      this.previewVisible = false
      this.visible = true
      this.pageNum = 1
      this.loadNotices(item ? this.noticeKey(item) : '')
    },
    changePage (page) { this.pageNum = page; this.loadNotices() },
    changeSize (size) { this.pageSize = size; this.pageNum = 1; this.loadNotices() },
    displayTime (value) { return value ? String(value).replace('T', ' ').slice(0, 16) : '' },
    noticeKey (item) { return `${item.notificationType || 'SYSTEM_NOTICE'}:${item.id}` },
    messageTypeText (type) {
      return {
        BILLING_DEPOSIT_DEBIT: translateUi('账单扣款'),
        BILLING_INSUFFICIENT: translateUi('账单待补款'),
        RECHARGE_REVERSAL: translateUi('充值撤销'),
        DEPOSIT_DEBIT: translateUi('押金扣款'),
        DISPUTE_STATUS: translateUi('争议进度'),
        CHARGE_RELEASE: translateUi('冻结金额释放'),
        ORDER_DELETE_REFUND: translateUi('删除订单退款')
      }[type] || '资金消息'
    },
    selectNotice (item) {
      this.selectedId = this.noticeKey(item)
      if (!item.unread) return
      markCustomerNotificationRead(item.id).then(() => {
        this.$set(item, 'unread', 0)
        this.unreadTotal = Math.max(0, this.unreadTotal - 1)
        const previewItem = this.recentNotices.find(entry => this.noticeKey(entry) === this.noticeKey(item))
        if (previewItem) this.$set(previewItem, 'unread', 0)
      }).catch(() => {})
    },
    loadPreview () {
      const requestId = ++this.previewRequestId
      this.previewLoading = true
      listCustomerNotifications({ pageNum: 1, pageSize: 6, scope: 'MESSAGE' }).then(data => {
        if (requestId !== this.previewRequestId) return
        this.recentNotices = data.list || []
        this.previewTotal = Number(data.total || 0)
        this.unreadTotal = Number(data.unread || 0)
      }).catch(() => {
        if (requestId !== this.previewRequestId) return
        this.recentNotices = []
        this.previewTotal = 0
      }).finally(() => {
        if (requestId === this.previewRequestId) this.previewLoading = false
      })
    },
    loadNotices (preferredId) {
      const requestId = ++this.requestId
      this.loading = true
      listCustomerNotifications({ pageNum: this.pageNum, pageSize: this.pageSize, scope: 'MESSAGE' }).then(data => {
        if (requestId !== this.requestId) return
        this.notices = data.list || []
        this.total = Number(data.total || 0)
        this.unreadTotal = Number(data.unread || 0)
        const preferred = preferredId && this.notices.find(item => this.noticeKey(item) === preferredId)
        this.selectedId = preferred ? preferredId : (this.notices.length ? this.noticeKey(this.notices[0]) : '')
        if (preferred) this.selectNotice(preferred)
      }).catch(() => {
        if (requestId !== this.requestId) return
        this.notices = []
        this.total = 0
        this.unreadTotal = 0
        this.selectedId = ''
      }).finally(() => {
        if (requestId === this.requestId) this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.customer-notices { display: inline-flex; height: 56px; align-items: center; vertical-align: top; margin-right: 14px; }
.notice-badge { line-height: 1; }
.notice-trigger { width: 36px; height: 36px; padding: 0; color: #4d625e; font-size: 20px; }
.notice-trigger:hover { background: #edf7f4; color: #168a7c; }
.notices-dialog { line-height: 1.5; }
.notices-dialog ::v-deep .el-dialog { max-width: calc(100vw - 24px); }
.notices-dialog ::v-deep .el-dialog__body { padding: 0 20px 18px; }
.notices-title { color: #243735; font-size: 18px; font-weight: 600; }
.notices-title small { margin-left: 10px; color: #82918e; font-size: 12px; font-weight: 400; }
.notices-body { display: flex; min-height: 300px; border-top: 1px solid #e7eeec; }
.notices-body > .el-empty { width: 100%; }
.notice-list { width: 230px; flex: none; max-height: 420px; overflow-y: auto; border-right: 1px solid #e7eeec; }
.notice-item { display: block; width: 100%; padding: 14px 12px; border: 0; border-bottom: 1px solid #edf1f0; text-align: left; background: #fff; cursor: pointer; }
.notice-item:hover, .notice-item.selected { background: #f0f8f5; }
.notice-title { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #243735; font-size: 13px; font-weight: 600; }
.notice-meta { display: block; margin-top: 5px; color: #768783; font-size: 11px; }
.notice-detail { flex: 1; min-width: 0; padding: 15px 0 0 20px; }
.notice-detail h3 { margin: 0; color: #243735; font-size: 16px; line-height: 1.4; overflow-wrap: anywhere; }
.notice-detail-meta { margin: 5px 0 14px; color: #768783; font-size: 12px; }
.notice-content { display: block; width: 100%; height: 320px; border: 0; }
.notice-pagination { display: flex; justify-content: flex-end; padding-top: 12px; }
@media (max-width: 600px) {
  .customer-notices { margin-right: 4px; }
  .notice-list { width: 100%; max-height: 150px; border-right: 0; border-bottom: 1px solid #e7eeec; }
  .notices-body { flex-direction: column; }
  .notice-detail { padding: 14px 0 0; }
  .notice-content { height: 220px; }
}
</style>

<style>
.customer-message-popover { padding: 0 !important; border-color: #e0eae7 !important; border-radius: 10px !important; box-shadow: 0 10px 30px rgba(24, 58, 53, .16) !important; overflow: hidden; }
.message-preview { min-height: 120px; color: #263b38; line-height: 1.4; }
.message-preview-heading { display: flex; align-items: center; justify-content: space-between; padding: 15px 16px 12px; border-bottom: 1px solid #edf2f0; }
.message-preview-heading strong { font-size: 15px; }
.message-preview-heading span { color: #d67c2f; font-size: 12px; }
.message-preview-list { max-height: 360px; overflow-y: auto; }
.message-preview-item { display: flex; align-items: flex-start; gap: 10px; width: 100%; padding: 12px 16px; border: 0; border-bottom: 1px solid #f0f3f2; background: #fff; text-align: left; cursor: pointer; }
.message-preview-item:hover { background: #f3f9f7; }
.message-preview-item > i:first-child { margin-top: 2px; color: #3d8d81; font-size: 16px; }
.message-preview-item > span { min-width: 0; flex: 1; }
.message-preview-item strong, .message-preview-item small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.message-preview-item strong { color: #263b38; font-size: 13px; }
.message-preview-item small { margin-top: 4px; color: #82918e; font-size: 11px; }
.message-preview-item em { width: 7px; height: 7px; margin-top: 5px; border-radius: 50%; background: #e3903c; flex: none; }
.message-preview-all { display: flex; align-items: center; justify-content: center; width: 100%; padding: 12px; border: 0; background: #f8fbfa; color: #277c70; font-size: 13px; cursor: pointer; }
.message-preview-all:hover { background: #eaf5f2; }
.message-preview-all i { margin-left: 5px; }
@media (max-width: 480px) { .customer-message-popover { left: 12px !important; right: 12px !important; width: auto !important; } }
</style>
